import { useQuery } from '@tanstack/react-query';
import { DynamicUiEntitiesMetadata } from 'src/UI/dynamicUI/types';
import {
  IconTaxPound,
  IconReceiptPound,
  IconFreeRights,
  IconProps,
  Icon,
  IconBuildingBank,
  IconBuildingEstate,
  IconDiamond,
  IconCoins,
  IconCoinPound,
  IconBuilding,
  IconUmbrella,
} from '@tabler/icons-react';
import { ForwardRefExoticComponent, RefAttributes } from 'react';

const spendingAccountTypes = {
  SpendingAccount: [
    'CURRENT_ACCOUNT',
    'CREDIT_CARD',
    'CHILDREN_ACCOUNT',
    'JOINT_ACCOUNT',
    'BUSINESS_ACCOUNT',
    'BUSINESS_CREDIT_CARD',
  ],
};

const liabilityTypes = {
  Loan: [
    'CAR_FINANCE',
    'DEBT_CONSOLIDATION_LOAN',
    'PERSONAL_LOAN',
    'POINT_OF_SALE_CREDIT',
    'STUDENT_LOAN',
    'UNSTRUCTURED_DEBT',
  ],
  Mortgage: ['BUY_TO_LET', 'COMMERCIAL', 'RESIDENTIAL'],
};

const protectionTypes = {
  Protection: ['CRITICAL_ILLNESS', 'DISABILITY', 'INCOME_PROTECTION', 'LIFE', 'PRIVATE_MEDICAL'],
};

const financialAssetTypes = {
  Pension: {
    DefinedContributionPension: [
      'INDIVIDUAL_EXECUTIVE_PENSION_PLAN',
      'MASTER_TRUST',
      'PERSONAL_PENSION',
      'SECTION_32_BUYOUT',
      'SELF_INVESTED_PERSONAL_PENSION',
      'STAKEHOLDER_PENSION',
      'WORKPLACE_PENSION',
    ],
    DefinedBenefitPension: [
      'CAREER_AVERAGE_REVALUED_EARNINGS',
      'FINAL_SALARY_SCHEME',
      'PUBLIC_SECTOR_PENSION_SCHEME',
    ],
    PensionAnnuity: ['ANNUITY'],
    IncomeDrawdownPension: ['INCOME_DRAWDOWN'],
  },
  Property: [
    'HOUSE_IN_MULTIPLE_OCCUPATION',
    'MAIN_RESIDENCE',
    'RENTAL',
    'SECOND_HOME',
    'SHARED_OWNERSHIP',
    'STUDENT_ACCOMMODATION_UNITS',
  ],
  InvestmentAndCash: [
    'CASH_SAVINGS',
    'DISCRETIONARY_PORTFOLIO',
    'DIRECT',
    'GENERAL_INVESTMENT_ACCOUNT',
    'INVESTMENT_BOND',
    'INVESTMENT_TRUSTS',
    'ISA_CASH',
    'ISA_JUNIOR_S_AND_S',
    'ISA_JUNIOR_CASH',
    'ISA_LIFETIME_S_AND_S',
    'ISA_LIFETIME_CASH',
    'ISA_STOCKS_AND_SHARES',
    'OFFSHORE_BOND',
    'STRUCTURED_PRODUCT',
    'UNREGULATED_COLLECTIVE_INVESTMENT_SCHEME',
    'VENTURE_CAPITAL_TRUST',
    'WRAP',
  ],
  Collectible: [
    'ART',
    'BOOKS',
    'CAR',
    'CARAVAN',
    'COINS',
    'JEWELLERY',
    'MUSICAL_INSTRUMENT',
    'PRECIOUS_METAL',
    'STAMPS',
    'WATCH',
    'OTHER',
  ],
  Crypto: [
    'AI_INFRASTRUCTURE_AND_EMERGING_TOKENS',
    'CRYPTOCURRENCIES',
    'DEFI_TOKENS',
    'NFT_AND_METAVERSE_TOKENS',
    'STABLECOINS',
    'OTHER',
  ],
} as const;

export const categoriesToProductsToType = {
  ASSET: financialAssetTypes,
  PROTECTION: protectionTypes,
  SPENDING_ACCOUNT: spendingAccountTypes,
  LIABILITY: liabilityTypes,
};

type ProductGroupMap = {
  ASSET: keyof typeof financialAssetTypes;
  PROTECTION: keyof typeof protectionTypes;
  SPENDING_ACCOUNT: keyof typeof spendingAccountTypes;
  LIABILITY: keyof typeof liabilityTypes;
};

type ProductInfo = {
  types: string[];
  label: string;
  description: string;
  icon?: ForwardRefExoticComponent<IconProps & RefAttributes<Icon>>;
};

export const financialCategories = [
  'SPENDING_ACCOUNT',
  'ASSET',
  'LIABILITY',
  'PROTECTION',
] as const;
export type FinancialCategory = (typeof financialCategories)[number];

export type CategoryInfo = {
  products: { [product: string]: ProductInfo | CategoryInfo };
  label: string;
  icon: ForwardRefExoticComponent<IconProps & RefAttributes<Icon>>;
  productsOrder: string[];
};

export const categoryToDisplayName: Record<FinancialCategory, string> = {
  SPENDING_ACCOUNT: 'Spending Accounts',
  ASSET: 'Assets',
  LIABILITY: 'Liabilities',
  PROTECTION: 'Policy',
};

export const financialAssetOptions: { [category: string]: CategoryInfo } = {
  SPENDING_ACCOUNT: {
    products: {
      SpendingAccount: {
        types: [
          'CURRENT_ACCOUNT',
          'CREDIT_CARD',
          'CHILDREN_ACCOUNT',
          'JOINT_ACCOUNT',
          'BUSINESS_ACCOUNT',
          'BUSINESS_CREDIT_CARD',
        ],
        label: 'Spending account',
        description:
          'Spending accounts are accounts that are used to store money, such as a bank account or credit card.',
        icon: IconReceiptPound,
      },
    },
    label: 'Spending accounts',
    icon: IconReceiptPound,
    productsOrder: ['SpendingAccount'],
  },
  ASSET: {
    label: 'Assets',
    icon: IconTaxPound,
    productsOrder: ['InvestmentAndCash', 'Pension', 'Property', 'Collectible', 'Crypto'],
    products: {
      Pension: {
        icon: IconTaxPound,
        label: 'Pensions',
        productsOrder: [
          'DefinedContributionPension',
          'DefinedBenefitPension',
          'PensionAnnuity',
          'IncomeDrawdownPension',
        ],
        products: {
          DefinedContributionPension: {
            types: [
              'INDIVIDUAL_EXECUTIVE_PENSION_PLAN',
              'MASTER_TRUST',
              'PERSONAL_PENSION',
              'SECTION_32_BUYOUT',
              'SELF_INVESTED_PERSONAL_PENSION',
              'STAKEHOLDER_PENSION',
              'WORKPLACE_PENSION',
            ],
            label: 'Defined contribution',
            description:
              'A defined contribution pension is a retirement plan where contributions are invested to build a fund for retirement, with the final value depending on investment performance rather than a guaranteed payout.',
          },
          DefinedBenefitPension: {
            types: [
              'CAREER_AVERAGE_REVALUED_EARNINGS',
              'FINAL_SALARY_SCHEME',
              'PUBLIC_SECTOR_PENSION_SCHEME',
            ],
            label: 'Defined benefit',
            description:
              'Defined benefit details help reflect guaranteed income in  client’s plan — enabling more accurate, personalised advice.',
          },
          PensionAnnuity: {
            types: ['ANNUITY'],
            label: 'Annuity',
            description:
              'An annuity is a financial product that provides a guaranteed income for life or a set period, typically purchased with pension savings at retirement.',
          },
          IncomeDrawdownPension: {
            types: ['INCOME_DRAWDOWN'],
            label: 'Income drawdown',
            description:
              'Income drawdown is a way of using your pension pot to provide a regular retirement income by keeping it invested and withdrawing money as needed, rather than buying an annuity.',
          },
        },
      },
      Property: {
        icon: IconBuildingEstate,
        types: [
          'HOUSE_IN_MULTIPLE_OCCUPATION',
          'MAIN_RESIDENCE',
          'RENTAL',
          'SECOND_HOME',
          'SHARED_OWNERSHIP',
          'STUDENT_ACCOMMODATION_UNITS',
        ],
        label: 'Properties',
        description:
          'Property assets include residential and investment properties, providing value through ownership, rental income, or capital appreciation.',
      },
      InvestmentAndCash: {
        icon: IconBuildingBank,
        types: [
          'CASH_SAVINGS',
          'DISCRETIONARY_PORTFOLIO',
          'DIRECT',
          'GENERAL_INVESTMENT_ACCOUNT',
          'INVESTMENT_BOND',
          'INVESTMENT_TRUSTS',
          'ISA_CASH',
          'ISA_JUNIOR_S_AND_S',
          'ISA_JUNIOR_CASH',
          'ISA_LIFETIME_S_AND_S',
          'ISA_LIFETIME_CASH',
          'ISA_STOCKS_AND_SHARES',
          'OFFSHORE_BOND',
          'STRUCTURED_PRODUCT',
          'UNREGULATED_COLLECTIVE_INVESTMENT_SCHEME',
          'VENTURE_CAPITAL_TRUST',
          'WRAP',
        ],
        label: 'Cash and investments',
        description:
          'Investment & cash assets include savings accounts and a range of investment products, offering growth or income potential through various financial instruments.',
      },
      Collectible: {
        icon: IconDiamond,
        types: [
          'ART',
          'BOOKS',
          'CAR',
          'CARAVAN',
          'COINS',
          'JEWELLERY',
          'MUSICAL_INSTRUMENT',
          'PRECIOUS_METAL',
          'STAMPS',
          'WATCH',
          'OTHER',
        ],
        label: 'Collectibles and other valuables',
        description:
          'Collectibles and other valuables include items such as art, jewelry, vehicles, and rare objects, which may hold personal or investment value.',
      },
      Crypto: {
        icon: IconCoins,
        types: [
          'AI_INFRASTRUCTURE_AND_EMERGING_TOKENS',
          'CRYPTOCURRENCIES',
          'DEFI_TOKENS',
          'NFT_AND_METAVERSE_TOKENS',
          'STABLECOINS',
          'OTHER',
        ],
        label: 'Crypto',
        description:
          'Crypto assets include cryptocurrencies, tokens, and digital collectibles, representing value or utility on blockchain networks.',
      },
    },
  },
  LIABILITY: {
    label: 'Liabilities',
    productsOrder: ['Loan', 'Mortgage'],
    icon: IconFreeRights,
    products: {
      Loan: {
        icon: IconCoinPound,
        types: [
          'CAR_FINANCE',
          'DEBT_CONSOLIDATION_LOAN',
          'PERSONAL_LOAN',
          'POINT_OF_SALE_CREDIT',
          'STUDENT_LOAN',
          'UNSTRUCTURED_DEBT',
        ],
        label: 'Loans',
        description:
          'Loans are borrowed funds that must be repaid, often used for specific purposes like purchasing a car or consolidating debt, impacting financial health through interest and repayment obligations.',
      },
      Mortgage: {
        icon: IconBuilding,
        types: ['BUY_TO_LET', 'COMMERCIAL', 'RESIDENTIAL'],
        label: 'Mortgages',
        description:
          'Mortgages are loans that are paid off over a period of time, often used for buying a home.',
      },
    },
  },
  PROTECTION: {
    label: 'Protection',
    icon: IconUmbrella,
    productsOrder: ['Protection'],
    products: {
      Protection: {
        types: ['CRITICAL_ILLNESS', 'DISABILITY', 'INCOME_PROTECTION', 'LIFE', 'PRIVATE_MEDICAL'],
        label: 'Protection',
        description:
          'Protections are financial products that help protect against financial risks, such as a medical emergency or a disability.',
      },
    },
  },
};

// For pension product name
type PensionProduct = keyof (typeof financialAssetTypes)['Pension'];

// For each pension_product, get valid types
type TypeForPensionProduct<P extends PensionProduct> =
  (typeof financialAssetTypes)['Pension'][P][number];

// For non-pension products
type TypeForProduct<C extends keyof ProductGroupMap> = ProductGroupMap[C];

export type BaseFinancialItem = {
  id: string;
  product: string;
  category: string;
  type: string;
  logoUri: string;
  clientId: string;
  description?: string;
  notes?: string;
  provider?: string;
  createdAt: string;
};

export type Protection = BaseFinancialItem & {
  product: 'Protection';
  type: TypeForProduct<'PROTECTION'>;
  category: 'PROTECTION';
  termStartDate?: Date;
  termEndDate?: Date;
  premiumAmount?: number;
  coverAmount?: number;
};

type BaseLiability = BaseFinancialItem & {
  id: string;
  category: 'LIABILITY';
  remainingBalance: number;
  remainingBalanceDate: string;
};

type Loan = BaseLiability & {
  product: 'Loan';
  type: (typeof categoriesToProductsToType)['LIABILITY']['Loan'];
};

type Mortgage = BaseLiability & {
  product: 'Mortgage';
  type: (typeof categoriesToProductsToType)['LIABILITY']['Mortgage'];
};

type BaseSpendingAccount = BaseFinancialItem & {
  id: string;
  category: 'SPENDING_ACCOUNT';
  currentBalance: number;
  balanceDate: string;
};

export type SpendingAccount = BaseSpendingAccount & {
  product: 'PersonalSpendingAccount';
  type: (typeof categoriesToProductsToType)['SPENDING_ACCOUNT']['SpendingAccount'];
};

type BaseFinancialAsset = BaseFinancialItem & {
  category: 'ASSET';
  value: number;
  valueDate: string;
};

// Pension variants based on pension_product
export type PensionAsset = BaseFinancialAsset & {
  product: 'Pension';
  pensionType: string;
  providerReferenceNumber?: string;
} & (
    | {
        pensionProduct: 'DefinedContributionPension';
        type: TypeForPensionProduct<'DefinedContributionPension'>;
      }
    | {
        pensionProduct: 'DefinedBenefitPension';
        type: TypeForPensionProduct<'DefinedBenefitPension'>;
      }
    | {
        pensionProduct: 'PensionAnnuity';
        type: TypeForPensionProduct<'PensionAnnuity'>;
      }
    | {
        pensionProduct: 'IncomeDrawdownPension';
        type: TypeForPensionProduct<'IncomeDrawdownPension'>;
      }
  );

// Property and investment assets remain unchanged
type PropertyAsset = BaseFinancialAsset & {
  product: 'Property';
  type: (typeof categoriesToProductsToType)['ASSET']['Property'];
  pension_product?: never;
};

type InvestmentAndCash = BaseFinancialAsset & {
  product: 'InvestmentAndCash';
  type: (typeof categoriesToProductsToType)['ASSET']['InvestmentAndCash'];
  pension_product?: never;
  providerReferenceNumber?: string;
};

type CollectibleAsset = BaseFinancialAsset & {
  product: 'Collectible';
  type: (typeof categoriesToProductsToType)['ASSET']['Collectible'];
  pension_product?: never;
};

type CryptoAsset = BaseFinancialAsset & {
  product: 'Crypto';
  type: (typeof categoriesToProductsToType)['ASSET']['Crypto'];
  pension_product?: never;
};

export type FinancialAsset =
  | PensionAsset
  | PropertyAsset
  | InvestmentAndCash
  | CollectibleAsset
  | CryptoAsset;

export type Liability = Loan | Mortgage;

export type FinancialItems = {
  assets: FinancialAsset[];
  protections: Protection[];
  spendingAccounts: SpendingAccount[];
  liabilities: Liability[];
  metadata: DynamicUiEntitiesMetadata;
};

export default function useFinancialItems(clientId: string) {
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<FinancialItems>({
    queryKey: ['clients', clientId, 'financial-items-bff'],
  });

  return {
    assets: data?.assets || [],
    protections: data?.protections || [],
    spendingAccounts: data?.spendingAccounts || [],
    liabilities: data?.liabilities || [],
    metadata: data?.metadata || {},
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  };
}
